import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, MessageSquare } from "lucide-react";
import {
  listNotificationTemplates,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  listNotificationTemplatesPortal,
  createNotificationTemplatePortal,
  updateNotificationTemplatePortal,
  deleteNotificationTemplatePortal,
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Textarea,
  Switch,
  Badge,
  Table,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  EmptyState,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const EVENT_TYPES = [
  "DUE_REMINDER",
  "OVERDUE_REMINDER",
  "RECEIPT",
  "PAYMENT_SUCCESS",
  "CUSTOM",
];

const CHANNELS = ["WHATSAPP", "SMS", "EMAIL"];

const TAGS = [
  "{{studentName}}",
  "{{amount}}",
  "{{outstanding}}",
  "{{dueDate}}",
  "{{receiptNo}}",
  "{{admissionNo}}",
];

const CHANNEL_OPTIONS = CHANNELS.map((c) => ({ label: c, value: c }));
const EVENT_TYPE_OPTIONS = EVENT_TYPES.map((et) => ({
  label: et.replace(/_/g, " "),
  value: et,
}));

const emptyForm = {
  name: "",
  channel: "WHATSAPP",
  eventType: "DUE_REMINDER",
  subject: "",
  body: "",
  isActive: true,
};

const NotificationTemplateManager = ({ isPortal = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const bodyRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await listNotificationTemplatesPortal()
        : await listNotificationTemplates();
      setTemplates(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const startEdit = (tpl) => {
    setEditingId(tpl._id);
    setForm({
      name: tpl.name,
      channel: tpl.channel,
      eventType: tpl.eventType,
      subject: tpl.subject || "",
      body: tpl.body,
      isActive: tpl.isActive,
    });
  };

  const startNew = () => {
    setEditingId("new");
    setForm(emptyForm);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const insertTag = (tag) => {
    const el = bodyRef.current;
    if (!el) {
      setForm((p) => ({ ...p, body: p.body + tag }));
      return;
    }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + tag + form.body.slice(end);
    setForm((p) => ({ ...p, body: next }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) {
      toast.error("Name and Message Body are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        if (isPortal) await createNotificationTemplatePortal(form);
        else await createNotificationTemplate(form);
        toast.success("Template created.");
      } else {
        if (isPortal) await updateNotificationTemplatePortal(editingId, form);
        else await updateNotificationTemplate(editingId, form);
        toast.success("Template updated.");
      }
      cancelEdit();
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      if (isPortal) await deleteNotificationTemplatePortal(id);
      else await deleteNotificationTemplate(id);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed.");
    }
  };

  const handleToggleActive = async (tpl) => {
    try {
      const payload = { isActive: !tpl.isActive };
      if (isPortal) await updateNotificationTemplatePortal(tpl._id, payload);
      else await updateNotificationTemplate(tpl._id, payload);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to toggle template.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (tpl) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{tpl.name}</span>
      ),
    },
    {
      key: "channel",
      header: "Channel",
      render: (tpl) => <Badge variant="primary">{tpl.channel}</Badge>,
    },
    {
      key: "eventType",
      header: "Event",
      render: (tpl) => (
        <span className="text-neutral-700 dark:text-neutral-200 font-medium">
          {tpl.eventType.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      render: (tpl) => (
        <button
          type="button"
          onClick={() => handleToggleActive(tpl)}
          className="focus:outline-none"
        >
          <Badge variant={tpl.isActive ? "success" : "default"}>
            {tpl.isActive ? "Active" : "Inactive"}
          </Badge>
        </button>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (tpl) => (
        <div className="flex items-center justify-end gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => startEdit(tpl)}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-danger-500 hover:text-danger-700"
            onClick={() => handleDelete(tpl._id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b bg-neutral-50/50">
        <div>
          <CardTitle>Notification Templates</CardTitle>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
            Configure default text templates and dynamic tag placeholders.
          </p>
        </div>
        {editingId === null && (
          <Button
            type="button"
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={startNew}
          >
            New Template
          </Button>
        )}
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        
        

        {editingId !== null && (
          <form
            onSubmit={handleSave}
            className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 p-4 space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Template Name *"
                placeholder="Template name"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                required
              />

              <Select
                label="Channel *"
                options={CHANNEL_OPTIONS}
                value={form.channel}
                onChange={(val) => setForm((p) => ({ ...p, channel: val }))}
              />

              <Select
                label="Event Type *"
                options={EVENT_TYPE_OPTIONS}
                value={form.eventType}
                onChange={(val) => setForm((p) => ({ ...p, eventType: val }))}
              />
            </div>

            {form.channel === "EMAIL" && (
              <Input
                label="Subject"
                placeholder="Subject line for email..."
                value={form.subject}
                onChange={(e) =>
                  setForm((p) => ({ ...p, subject: e.target.value }))
                }
              />
            )}

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mr-1">
                  Insert Tag:
                </span>
                {TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => insertTag(tag)}
                    className="inline-flex items-center rounded border border-primary-200 bg-primary-50 px-2 py-1 text-[11px] font-mono text-primary-900 dark:text-primary-300 transition-colors hover:bg-primary-100"
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <Textarea
                ref={bodyRef}
                label="Message Body *"
                rows={4}
                placeholder="Message body — click a tag above to insert it at the cursor position."
                value={form.body}
                onChange={(e) =>
                  setForm((p) => ({ ...p, body: e.target.value }))
                }
                className="font-mono text-xs"
                required
              />
            </div>

            <div className="pt-1">
              <Switch
                label="Active"
                checked={form.isActive}
                onChange={(checked) =>
                  setForm((p) => ({ ...p, isActive: checked }))
                }
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-neutral-200 dark:border-neutral-800 pt-3">
              <Button
                type="button"
                variant="secondary"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" isLoading={saving}>
                Save Template
              </Button>
            </div>
          </form>
        )}

        <Table
          columns={columns}
          data={templates}
          isLoading={loading}
          emptyState={
            <EmptyState
              icon={MessageSquare}
              title="No templates found"
              description="Create a notification template to start automating reminders and receipts."
              action={
                <Button
                  type="button"
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={startNew}
                >
                  New Template
                </Button>
              }
            />
          }
        />
      </CardContent>
    </Card>
  );
};

export default NotificationTemplateManager;